import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText, Loader2, Upload, AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { formatApiError } from "../lib/api";
import SEO from "../components/SEO";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PDF_MB = 10;

export default function ConsultoriaForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    tax_id: "",
    company: "",
    billing_address: "",
    auction_id: "",
    additional_info: "",
  });
  const [pdf, setPdf] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setError("");
    if (!f) { setPdf(null); return; }
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setError("El archivo debe ser un PDF.");
      setPdf(null); return;
    }
    if (f.size > MAX_PDF_MB * 1024 * 1024) {
      setError(`El PDF supera ${MAX_PDF_MB} MB.`);
      setPdf(null); return;
    }
    setPdf(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pdf) { setError("Adjunta la nota simple registral actualizada (PDF)."); return; }
    if (!accepted) { setError("Debes aceptar las condiciones para continuar."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("origin_url", window.location.origin);
      fd.append("pdf", pdf);
      fd.append("consent", "true");
      fd.append("website", website);  // honeypot
      const res = await fetch(`${API_BASE}/consultoria/submit`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail) || "Error al enviar.");
      if (!data.url) throw new Error("No se pudo crear la sesión de pago.");
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || "Error al enviar el formulario.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Contratar Consultoría de Deudas · 95 €"
        description="Sube la nota simple y los datos de facturación para contratar la Consultoría de Deudas. Pago seguro con Stripe e informe entregado en 24-72 horas."
        path="/consultoria/contratar"
      />
      <SiteHeader />

      {/* HERO */}
      <section className="bg-[#2d1f1a] text-white">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">Consultoría de Deudas · 95 € · IVA incluido</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-3">
            Rellena tus datos y adjunta la nota simple.
          </h1>
          <p className="text-stone-400 text-sm sm:text-base leading-relaxed max-w-3xl">
            Completa el formulario, sube la nota simple <strong className="text-white">actualizada a fecha de hoy</strong> y procede al pago seguro. En cuanto recibamos el pago empezamos el análisis y te enviamos las conclusiones a tu email en <strong className="text-white">24-72 horas hábiles</strong>.
          </p>
        </div>
      </section>

      {/* FORM */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <form onSubmit={onSubmit} className="grid lg:grid-cols-3 gap-8" data-testid="consultoria-form">
          {/* COLUMNA IZQUIERDA — formulario */}
          <div className="lg:col-span-2 bg-white border border-stone-200 rounded-sm p-8 space-y-6">
            <div>
              <SectionTitle n="01" title="Datos de facturación" />
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <Field label="Nombre y apellidos *" required>
                  <input value={form.full_name} onChange={update("full_name")} required data-testid="input-fullname"
                    className="form-input" autoComplete="name" />
                </Field>
                <Field label="Email *" required>
                  <input type="email" value={form.email} onChange={update("email")} required data-testid="input-email"
                    className="form-input" autoComplete="email" />
                </Field>
                <Field label="Teléfono">
                  <input value={form.phone} onChange={update("phone")} data-testid="input-phone"
                    className="form-input" autoComplete="tel" placeholder="+34 ..." />
                </Field>
                <Field label="NIF / CIF *" required>
                  <input value={form.tax_id} onChange={update("tax_id")} required data-testid="input-taxid"
                    className="form-input" />
                </Field>
                <Field label="Razón social (si aplica)">
                  <input value={form.company} onChange={update("company")} data-testid="input-company"
                    className="form-input" />
                </Field>
                <Field label="Dirección de facturación *" required>
                  <input value={form.billing_address} onChange={update("billing_address")} required data-testid="input-address"
                    className="form-input" placeholder="Calle, número, CP, ciudad, provincia" />
                </Field>
              </div>
            </div>

            <div className="pt-2 border-t border-stone-200">
              <SectionTitle n="02" title="Datos de la subasta" />
              <div className="grid gap-4 mt-4">
                <Field label="ID de la subasta (BOE) *" required>
                  <input value={form.auction_id} onChange={update("auction_id")} required data-testid="input-auction"
                    className="form-input" placeholder="Ej: SUB-AB-2025-12345" />
                </Field>
                <Field label="Información adicional (opcional)">
                  <textarea value={form.additional_info} onChange={update("additional_info")} data-testid="input-info"
                    rows={4} className="form-input resize-none"
                    placeholder="Detalles que consideres relevantes: diligencias, contexto, dudas concretas, edicto..." />
                </Field>
              </div>
            </div>

            <div className="pt-2 border-t border-stone-200">
              <SectionTitle n="03" title="Nota simple registral · PDF" />
              <p className="text-xs text-stone-600 mt-2 mb-4 leading-relaxed">
                Sube la <strong>nota simple actualizada a fecha de hoy</strong>. No usamos la certificación de cargas del BOE: solo la nota simple registral garantiza precisión.
              </p>
              <label className={`flex items-center gap-4 border-2 border-dashed rounded-sm p-5 cursor-pointer transition-colors ${pdf ? "border-[#E0651F] bg-[#E0651F]/5" : "border-stone-300 hover:border-[#E0651F]"}`} data-testid="pdf-dropzone">
                <input type="file" accept="application/pdf,.pdf" onChange={onFileChange} className="hidden" data-testid="input-pdf" />
                <div className="w-11 h-11 bg-[#2d1f1a] flex items-center justify-center rounded-sm flex-shrink-0">
                  {pdf ? <CheckCircle2 className="w-5 h-5 text-[#E0651F]" /> : <Upload className="w-5 h-5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  {pdf ? (
                    <>
                      <div className="font-display font-bold text-sm text-[#2d1f1a] truncate">{pdf.name}</div>
                      <div className="text-[11px] tracking-[0.15em] uppercase text-[#E0651F] font-bold mt-0.5">{(pdf.size / 1024 / 1024).toFixed(2)} MB · pulsa para cambiar</div>
                    </>
                  ) : (
                    <>
                      <div className="font-display font-bold text-sm text-[#2d1f1a]">Pulsa para subir tu PDF</div>
                      <div className="text-xs text-stone-500 mt-0.5">Solo PDF · máximo {MAX_PDF_MB} MB</div>
                    </>
                  )}
                </div>
              </label>
            </div>

            <label className="flex items-start gap-3 pt-4 border-t border-stone-200 cursor-pointer" data-testid="terms-check-label">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 accent-[#E0651F]" data-testid="terms-check" />
              <span className="text-xs text-stone-600 leading-relaxed">
                He leído y acepto las <a href="/condiciones-contratacion" target="_blank" rel="noopener noreferrer" className="text-[#E0651F] font-bold hover:underline">Condiciones de Contratación</a> y la <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-[#E0651F] font-bold hover:underline">Política de Privacidad</a>. Entiendo que el informe se entrega en <strong>24-72 horas hábiles</strong> tras la confirmación del pago, confirmo que la nota simple es <strong>actualizada</strong> y autorizo el tratamiento de mis datos para emitir la factura y enviar las conclusiones, conforme al RGPD y a la LOPDGDD.
              </span>
            </label>
            {/* Honeypot */}
            <div style={{ position: "absolute", left: "-10000px", height: 0, overflow: "hidden" }} aria-hidden="true">
              <label>Sitio web<input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
            </div>

            {error && (
              <div className="flex items-start gap-3 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-sm" data-testid="form-error">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* COLUMNA DERECHA — resumen + botón */}
          <aside className="lg:col-span-1">
            <div className="sticky top-20 bg-[#2d1f1a] text-white rounded-sm p-7 border-l-4 border-[#E0651F]">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">Resumen del pedido</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-5xl font-black">95 €</span>
                <span className="text-stone-400 text-sm">IVA incl.</span>
              </div>
              <div className="text-xs text-stone-400 mb-4">Pago único · sin permanencia.</div>
              <div className="bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-[11px] font-mono text-stone-300 space-y-1 mb-5" data-testid="iva-breakdown-form">
                <div className="flex justify-between"><span>Base</span><span>78,51 €</span></div>
                <div className="flex justify-between"><span>IVA (21 %)</span><span>16,49 €</span></div>
                <div className="flex justify-between font-bold text-white pt-1 border-t border-white/15"><span>Total</span><span>95,00 €</span></div>
              </div>
              <ul className="text-sm text-stone-300 space-y-2 mb-6">
                <Bullet>Análisis registral profesional</Bullet>
                <Bullet>Cargas que se cancelan / subsisten</Bullet>
                <Bullet>Detección de riesgos no visibles en BOE</Bullet>
                <Bullet>Entrega en 24-72 horas hábiles</Bullet>
              </ul>
              <button type="submit" disabled={loading} data-testid="submit-pay-btn"
                className="w-full bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] disabled:opacity-60 disabled:hover:bg-[#E0651F] disabled:hover:text-white text-white py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirigiendo…</> : <>Pagar 95 € y enviar <ArrowRight className="w-4 h-4" /></>}
              </button>
              <div className="mt-4 text-[10px] tracking-[0.18em] uppercase text-stone-500 flex items-center justify-center gap-2">
                <Lock className="w-3 h-3" /> Stripe · Tarjeta + Bizum
              </div>
            </div>
          </aside>
        </form>
      </section>

      <SiteFooter />

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.625rem 0.75rem;
          font-size: 0.875rem;
          background: #fff;
          border: 1px solid #d6d3d1;
          border-radius: 2px;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .form-input:focus {
          border-color: #E0651F;
          box-shadow: 0 0 0 2px rgba(224,101,31,0.15);
        }
      `}</style>
    </div>
  );
}

function SectionTitle({ n, title }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#E0651F]">{n}</span>
      <h2 className="font-display font-black text-lg text-[#2d1f1a] tracking-tight">{title}</h2>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <ShieldCheck className="w-4 h-4 text-[#E0651F] flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}
